/**
 * Event registry — the single source of truth for analytics event names and property schemas.
 *
 * Rules:
 * - All call sites use EVENTS.* constants. String literals in track() calls are forbidden.
 * - Every event in EVENTS must have a matching entry in SCHEMAS.
 * - SCHEMAS describes the expected property shape; validateEvent() runs in dev and warns on drift.
 *
 * See plan §3 for the full tracking plan.
 */

/* eslint-disable sort-keys */

// -----------------------------------------------------------------------------
// Event names (snake_case)
// -----------------------------------------------------------------------------
export const EVENTS = {
  // --- Marketing (§3.1) ---
  HOMEPAGE_VIEWED: "homepage_viewed",
  HOMEPAGE_SECTION_VIEWED: "homepage_section_viewed",
  HOMEPAGE_CTA_CLICKED: "homepage_cta_clicked",

  // --- Creator funnel (§3.2) ---
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",
  CREATOR_ONBOARDED: "creator_onboarded",
  GAME_CREATE_STARTED: "game_create_started",
  GAME_CREATE_SUBMITTED: "game_create_submitted",
  GAME_CREATE_SUCCEEDED: "game_create_succeeded",
  GAME_CREATE_AI_PARAMS_SUBMITTED: "game_create_ai_params_submitted",
  AI_GENERATION_STARTED: "ai_generation_started",
  AI_GENERATION_PHASE_COMPLETED: "ai_generation_phase_completed",
  AI_GENERATION_COMPLETED: "ai_generation_completed",
  AI_GENERATION_FAILED: "ai_generation_failed",
  GAME_CREATE_CANCELED: "game_create_canceled",
  QUESTION_REGENERATED: "question_regenerated",
  CATEGORY_EDITED: "category_edited",
  GAME_EDITOR_OPENED: "game_editor_opened",
  GAME_DETAILS_SAVED: "game_details_saved",
  CONTROLLER_OPENED: "controller_opened",
  GAMEBOARD_OPENED: "gameboard_opened",
  QUESTION_COMPLETED: "question_completed",
  GAME_STATE_CHANGED: "game_state_changed",
  GAME_COMPLETED: "game_completed",
  GAME_ABANDONED: "game_abandoned",
  GAME_NEVER_STARTED: "game_never_started",
  GAME_RESET: "game_reset",
  QUESTION_RESET: "question_reset",

  // --- Round Editor ---
  CLUE_EDIT_OPENED: "clue_edit_opened",
  CLUE_SAVED: "clue_saved",
  CATEGORY_RENAME_OPENED: "category_rename_opened",
  CATEGORY_RENAMED: "category_renamed",
  PANEL_CANCELLED: "panel_cancelled",

  // --- Player funnel (§3.3) ---
  JOIN_PAGE_VIEWED: "join_page_viewed",
  ROOM_CODE_SUBMITTED: "room_code_submitted",
  PLAYER_JOINED_GAME: "player_joined_game",
  PLAYER_NAME_SUBMITTED: "player_name_submitted",
  ANSWER_SUBMITTED: "answer_submitted",
  SUBMISSION_SCORED: "submission_scored",
  PLAYER_ABANDONED_GAME: "player_abandoned_game",
  PLAYER_BECAME_CREATOR: "player_became_creator",

  // --- Share cards (superlatives finale) ---
  SUPERLATIVES_FINALE_VIEWED: "superlatives_finale_viewed",
  SHARE_CARD_VIEWED: "share_card_viewed",
  SHARE_INITIATED: "share_initiated",
  SHARE_COMPLETED: "share_completed",
  SHARE_DISMISSED: "share_dismissed",
  SHARE_FAILED: "share_failed",
  SHARE_DOWNLOADED: "share_downloaded",

  // --- Host experience quality (§3.4) ---
  NARRATION_PLAYED: "narration_played",
  NARRATION_SKIPPED: "narration_skipped",
  NARRATION_GENERATION_FAILED: "narration_generation_failed",
  TTS_AUDIO_ERROR: "tts_audio_error",

  // --- Errors (§3.5) ---
  ERROR_OCCURRED: "error_occurred",

  // --- Stripe (§3.6) — reserved, not fired yet ---
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_COMPLETED: "checkout_completed",
  SUBSCRIPTION_CREATED: "subscription_created",
  SUBSCRIPTION_RENEWED: "subscription_renewed",
  SUBSCRIPTION_CANCELED: "subscription_canceled",
  SUBSCRIPTION_PLAN_CHANGED: "subscription_plan_changed",
  ONE_OFF_PURCHASE_COMPLETED: "one_off_purchase_completed",
  PAYWALL_VIEWED: "paywall_viewed",
  PAYWALL_DISMISSED: "paywall_dismissed",
};

// -----------------------------------------------------------------------------
// Property schemas
//
// Shape: { propName: "type" } where type is a hint string. Unknown types are allowed.
// Optional props should be prefixed with ?. e.g. { "?reset_reason": "string" }.
// -----------------------------------------------------------------------------
export const SCHEMAS = {
  // --- Marketing ---
  [EVENTS.HOMEPAGE_VIEWED]: {
    "?page": "string",
    "?utm_source": "string",
    "?utm_medium": "string",
    "?utm_campaign": "string",
    "?utm_content": "string",
    "?referrer_domain": "string",
  },
  [EVENTS.HOMEPAGE_SECTION_VIEWED]: {
    section_name: "string",
  },
  [EVENTS.HOMEPAGE_CTA_CLICKED]: {
    "?page": "string",
    cta_location: "string",
    destination: "string",
  },

  // --- Creator funnel ---
  [EVENTS.SIGNUP_STARTED]: {
    "?entry_point": "string",
    "?auth_route": "string",
    "?has_redirect_destination": "boolean",
  },
  [EVENTS.SIGNUP_COMPLETED]: { "?method": "string" },
  [EVENTS.CREATOR_ONBOARDED]: {},
  [EVENTS.GAME_CREATE_STARTED]: { creation_mode: "ai|manual" },
  [EVENTS.GAME_CREATE_SUBMITTED]: { creation_mode: "ai|manual" },
  [EVENTS.GAME_CREATE_SUCCEEDED]: {
    game_id: "string",
    creation_mode: "ai|manual",
  },
  [EVENTS.GAME_CREATE_AI_PARAMS_SUBMITTED]: {
    difficulty: "string",
    tone: "string",
    "?host_personality": "string",
    "?game_length_mode": "string",
    "?rounds": "number",
    category_count: "number",
    questions_per_category: "number",
    theme_prompt_length: "number",
    "?custom_category_count": "number",
  },
  [EVENTS.AI_GENERATION_STARTED]: {
    game_id: "string",
    creation_mode: "string",
    "?param_hash": "string",
  },
  [EVENTS.AI_GENERATION_PHASE_COMPLETED]: {
    game_id: "string",
    phase: "string",
    duration_ms: "number",
    "?input_tokens": "number",
    "?output_tokens": "number",
    "?model": "string",
    "?provider": "string",
  },
  [EVENTS.AI_GENERATION_COMPLETED]: {
    game_id: "string",
    total_duration_ms: "number",
    "?total_tokens": "number",
    "?total_cost_usd": "number",
    category_count: "number",
    question_count: "number",
  },
  [EVENTS.AI_GENERATION_FAILED]: {
    game_id: "string",
    phase: "string",
    error_code: "string",
    "?retry_count": "number",
  },
  [EVENTS.GAME_CREATE_CANCELED]: {
    game_id: "string",
    phase_at_cancel: "string",
    elapsed_seconds: "number",
    cancel_method: "string",
  },
  [EVENTS.QUESTION_REGENERATED]: {
    game_id: "string",
    question_id: "string",
    round: "string",
    regen_count_in_game: "number",
  },
  [EVENTS.CATEGORY_EDITED]: {
    game_id: "string",
    edit_type: "string",
  },
  [EVENTS.GAME_EDITOR_OPENED]: { game_id: "string", round: "string" },
  [EVENTS.GAME_DETAILS_SAVED]: {
    game_id: "string",
    fields_changed: "array",
  },
  [EVENTS.CONTROLLER_OPENED]: {
    game_id: "string",
    has_players: "boolean",
    player_count: "number",
    "?current_state": "string",
    "?round_state": "string",
    "?current_round": "string",
  },
  [EVENTS.GAMEBOARD_OPENED]: {
    game_id: "string",
    viewport_width: "number",
    viewport_height: "number",
    is_second_screen_likely: "boolean",
    "?current_state": "string",
    "?current_round": "string",
  },
  [EVENTS.QUESTION_COMPLETED]: {
    game_id: "string",
    question_id: "string",
    round: "string",
    category: "string",
    point_value: "number",
    submission_count: "number",
    correct_count: "number",
    incorrect_count: "number",
    was_daily_double: "boolean",
    time_from_read_to_complete_s: "number",
  },
  [EVENTS.GAME_STATE_CHANGED]: {
    game_id: "string",
    from_state: "string",
    to_state: "string",
    player_count: "number",
    "?elapsed_game_s": "number",
    "?is_game_start": "boolean",
  },
  [EVENTS.GAME_COMPLETED]: {
    game_id: "string",
    player_count: "number",
    total_duration_s: "number",
    questions_answered: "number",
    questions_skipped: "number",
    "?winner_score": "number",
  },
  [EVENTS.GAME_ABANDONED]: {
    game_id: "string",
    last_current_state: "string",
    last_round_state: "string",
    current_round: "string",
    player_count: "number",
    questions_answered: "number",
    minutes_since_last_activity: "number",
  },
  [EVENTS.GAME_NEVER_STARTED]: {
    game_id: "string",
    last_current_state: "string",
    player_count: "number",
    minutes_since_created: "number",
  },
  [EVENTS.GAME_RESET]: {
    game_id: "string",
    player_count: "number",
    current_state_before_reset: "string",
    round_state_before_reset: "string",
    current_round_before_reset: "string",
    questions_answered_before_reset: "number",
    minutes_since_game_created: "number",
  },
  [EVENTS.QUESTION_RESET]: {
    game_id: "string",
    question_id: "string",
    round: "string",
    category: "string",
    point_value: "number",
    round_state_before_reset: "string",
    had_submissions: "boolean",
    submission_count_before_reset: "number",
  },

  // --- Round Editor ---
  [EVENTS.CLUE_EDIT_OPENED]: {
    game_id: "string",
    round: "string",
    question_id: "string",
    was_empty: "boolean",
  },
  [EVENTS.CLUE_SAVED]: {
    game_id: "string",
    round: "string",
    question_id: "string",
    daily_double: "boolean",
    clue_length: "number",
    answer_count: "number",
  },
  [EVENTS.CATEGORY_RENAME_OPENED]: {
    game_id: "string",
    round: "string",
    category_idx: "number",
    clue_count: "number",
  },
  [EVENTS.CATEGORY_RENAMED]: {
    game_id: "string",
    round: "string",
    category_idx: "number",
    intent: "keep|erase",
  },
  [EVENTS.PANEL_CANCELLED]: {
    game_id: "string",
    round: "string",
    panel_mode: "clue|category",
  },

  // --- Player funnel ---
  [EVENTS.JOIN_PAGE_VIEWED]: { room_code_prefilled: "boolean" },
  [EVENTS.ROOM_CODE_SUBMITTED]: {
    code_length: "number",
    success: "boolean",
    "?failure_reason": "string",
  },
  [EVENTS.PLAYER_JOINED_GAME]: {
    game_id: "string",
    player_count_before: "number",
    is_returning_player: "boolean",
  },
  [EVENTS.PLAYER_NAME_SUBMITTED]: {
    name_length: "number",
  },
  [EVENTS.ANSWER_SUBMITTED]: {
    game_id: "string",
    question_id: "string",
    round: "string",
    "?category": "string",
    point_value: "number",
    answer_length: "number",
    "?time_to_submit_s": "number",
    "?is_daily_double": "boolean",
  },
  [EVENTS.SUBMISSION_SCORED]: {
    game_id: "string",
    question_id: "string",
    is_correct: "boolean",
    "?wager_amount": "number",
    new_player_score: "number",
  },
  [EVENTS.PLAYER_ABANDONED_GAME]: {
    game_id: "string",
    last_game_state: "string",
    seconds_in_game: "number",
  },
  [EVENTS.PLAYER_BECAME_CREATOR]: {
    first_played_at: "string",
    games_played_before_signup: "number",
    days_from_first_play_to_signup: "number",
  },

  // --- Share cards (superlatives finale) ---
  [EVENTS.SUPERLATIVES_FINALE_VIEWED]: {
    game_id: "string",
    player_rank: "number",
    is_tied: "boolean",
    player_count: "number",
    final_score: "number",
    cards_shown_count: "number",
  },
  [EVENTS.SHARE_CARD_VIEWED]: {
    game_id: "string",
    variant: "string",
    card_index: "number",
    cards_total: "number",
    player_rank: "number",
    is_tied: "boolean",
    "?superlative_id": "string",
  },
  [EVENTS.SHARE_INITIATED]: {
    game_id: "string",
    variant: "string",
    card_index: "number",
    cards_total: "number",
    player_rank: "number",
    is_tied: "boolean",
    share_method: "native_share|download",
    "?superlative_id": "string",
  },
  [EVENTS.SHARE_COMPLETED]: {
    game_id: "string",
    variant: "string",
    card_index: "number",
    cards_total: "number",
    player_rank: "number",
    is_tied: "boolean",
    share_method: "native_share|download",
    "?superlative_id": "string",
  },
  [EVENTS.SHARE_DISMISSED]: {
    game_id: "string",
    variant: "string",
    card_index: "number",
    cards_total: "number",
    player_rank: "number",
    is_tied: "boolean",
    share_method: "native_share|download",
    "?superlative_id": "string",
  },
  [EVENTS.SHARE_FAILED]: {
    game_id: "string",
    variant: "string",
    card_index: "number",
    cards_total: "number",
    player_rank: "number",
    is_tied: "boolean",
    share_method: "native_share|download",
    error_name: "string",
    "?superlative_id": "string",
  },
  [EVENTS.SHARE_DOWNLOADED]: {
    game_id: "string",
    variant: "string",
    card_index: "number",
    cards_total: "number",
    player_rank: "number",
    is_tied: "boolean",
    share_method: "native_share|download",
    "?superlative_id": "string",
  },

  // --- Host experience quality ---
  [EVENTS.NARRATION_PLAYED]: {
    moment_type: "string",
    "?duration_ms": "number",
    cache_hit: "boolean",
  },
  [EVENTS.NARRATION_SKIPPED]: {
    moment_type: "string",
    played_ms_before_skip: "number",
  },
  [EVENTS.NARRATION_GENERATION_FAILED]: {
    moment_type: "string",
    error_code: "string",
  },
  [EVENTS.TTS_AUDIO_ERROR]: {
    error_code: "string",
    stage: "string",
  },

  // --- Errors ---
  [EVENTS.ERROR_OCCURRED]: {
    error_message: "string",
    error_name: "string",
    "?component_stack": "string",
    "?route": "string",
    "?user_role": "string",
    "?error_code": "string",
    "?severity": "string",
  },

  // --- Stripe (reserved) ---
  [EVENTS.CHECKOUT_STARTED]: {
    plan_id: "string",
    billing_interval: "string",
    price_usd: "number",
    "?offer_context": "string",
  },
  [EVENTS.CHECKOUT_COMPLETED]: {
    plan_id: "string",
    amount_usd: "number",
    currency: "string",
    stripe_customer_id: "string",
    first_purchase: "boolean",
  },
  [EVENTS.SUBSCRIPTION_CREATED]: {
    plan_id: "string",
    interval: "string",
    "?trial_days": "number",
    mrr_usd: "number",
  },
  [EVENTS.SUBSCRIPTION_RENEWED]: {
    plan_id: "string",
    mrr_usd: "number",
    months_active: "number",
  },
  [EVENTS.SUBSCRIPTION_CANCELED]: {
    plan_id: "string",
    months_active: "number",
    "?cancel_reason_code": "string",
  },
  [EVENTS.SUBSCRIPTION_PLAN_CHANGED]: {
    from_plan: "string",
    to_plan: "string",
    mrr_delta_usd: "number",
  },
  [EVENTS.ONE_OFF_PURCHASE_COMPLETED]: {
    sku: "string",
    amount_usd: "number",
    "?bundle_size": "number",
  },
  [EVENTS.PAYWALL_VIEWED]: {
    trigger: "string",
    "?plan_shown": "string",
  },
  [EVENTS.PAYWALL_DISMISSED]: {
    seconds_on_paywall: "number",
  },
};

/**
 * Dev-only event validation. Returns an array of warning strings (empty if valid).
 * Call sites don't invoke this directly — the façade runs it in non-prod environments.
 */
export const validateEvent = (eventName, props = {}) => {
  const warnings = [];
  const knownNames = Object.values(EVENTS);
  if (!knownNames.includes(eventName)) {
    warnings.push(
      `Unknown event "${eventName}". Add it to EVENTS in src/analytics/events.js.`
    );
    return warnings;
  }
  const schema = SCHEMAS[eventName];
  if (!schema) {
    warnings.push(`Event "${eventName}" has no schema entry. Add one in SCHEMAS.`);
    return warnings;
  }
  const requiredKeys = Object.keys(schema).filter((k) => !k.startsWith("?"));
  for (const key of requiredKeys) {
    if (props[key] === undefined || props[key] === null) {
      warnings.push(`Event "${eventName}" missing required prop "${key}".`);
    }
  }
  // PII safety net — reject obvious PII field names on any event.
  const forbidden = [
    "email",
    "answer_text",
    "question_text",
    "signature",
    "clerk_token",
    "id_token",
    "access_token",
  ];
  for (const key of Object.keys(props)) {
    if (forbidden.some((f) => key.toLowerCase().includes(f))) {
      warnings.push(
        `Event "${eventName}" contains forbidden PII-adjacent prop "${key}".`
      );
    }
  }
  return warnings;
};
